import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Link2, Loader2, Share2, Trash2, UserPlus } from "lucide-react";
import { getOperatingSystemDetailPath } from "@/lib/operating-systems";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabase-function-errors";

type DirectLevel = "reader" | "editor" | "manager";
type PublicLevel = "none" | "reader" | "editor";

interface DirectGrant {
  userId: string;
  email: string | null;
  accessLevel: DirectLevel;
  createdAt: string;
  updatedAt: string;
}

interface SharingState {
  operatingSystemId: string;
  name: string | null;
  publicAccessLevel: PublicLevel;
  ownerUserId: string;
  ownerEmail: string | null;
  callerUserId: string;
  callerEffectiveLevel: string;
  isTrueOwner: boolean;
  directGrants: DirectGrant[];
}

const DIRECT_LABEL: Record<DirectLevel, string> = {
  reader: "Reader",
  editor: "Editor",
  manager: "Owner Permission",
};
const PUBLIC_LABEL: Record<PublicLevel, string> = {
  none: "No broad access",
  reader: "Reader broad access",
  editor: "Editor broad access",
};

function selectInputText(input: HTMLInputElement | null): void {
  if (!input) return;
  input.focus();
  input.select();
  input.setSelectionRange(0, input.value.length);
}

async function tryCopyTextToClipboard(text: string, selectedInput: HTMLInputElement | null): Promise<void> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // Continue to the selected-input fallback below.
  }

  selectInputText(selectedInput);
  if (typeof document === "undefined") return;

  try {
    document.execCommand("copy");
  } catch {
    // The selected visible input remains available for manual copy.
  }
}

export interface ShareOperatingSystemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operatingSystemId: string;
}

export function ShareOperatingSystemDialog({
  open,
  onOpenChange,
  operatingSystemId,
}: ShareOperatingSystemDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<SharingState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addEmail, setAddEmail] = useState("");
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  const shareUrl = useMemo(() => {
    const path = getOperatingSystemDetailPath(operatingSystemId);
    if (typeof window === "undefined") return path;
    return `${window.location.origin}${path}`;
  }, [operatingSystemId]);

  const call = useCallback(
    async (payload: Record<string, unknown>): Promise<SharingState | null> => {
      const { data, error: fnError } = await supabase.functions.invoke("manage-os-sharing", {
        body: { operatingSystemId, ...payload },
      });
      if (fnError) {
        const msg = await getSupabaseFunctionErrorMessage(fnError, "Sharing action failed");
        throw new Error(msg || "Sharing action failed");
      }
      const response = data as { error?: unknown } | null;
      const err = response?.error;
      if (err) throw new Error(String(err));
      return (data as SharingState) ?? null;
    },
    [operatingSystemId],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await call({ action: "list" });
      if (next) setState(next);
    } catch (e) {
      setState(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => {
    if (open && operatingSystemId) {
      void refresh();
    } else if (!open) {
      setState(null);
      setError(null);
      setAddEmail("");
    }
  }, [open, operatingSystemId, refresh]);

  const publicLevel = state?.publicAccessLevel ?? "none";
  const legacyPublicWarning = useMemo(() => {
    if (publicLevel === "editor") {
      return "Anyone with an authenticated Applyt account and this App link or ID can still change App data until this is disabled.";
    }
    if (publicLevel === "reader") {
      return "Anyone with an authenticated Applyt account and this App link or ID can still view and copy this App until this is disabled.";
    }
    return null;
  }, [publicLevel]);

  const onDisablePublic = async () => {
    setSaving(true);
    try {
      const next = await call({ action: "set_public", level: "none" });
      if (next) setState(next);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not disable broad access",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const onAddGrant = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = addEmail.trim();
    if (!email) return;
    setSaving(true);
    try {
      const next = await call({ action: "grant_user", email, level: "editor" });
      if (next) setState(next);
      setAddEmail("");
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not add user",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const onRevoke = async (userId: string) => {
    setPendingUserId(userId);
    try {
      const next = await call({ action: "revoke_user", userId });
      if (next) setState(next);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not revoke access",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setPendingUserId(null);
    }
  };

  const onCopyLink = async () => {
    selectInputText(linkInputRef.current);
    await tryCopyTextToClipboard(shareUrl, linkInputRef.current);
    selectInputText(linkInputRef.current);
    toast({
      title: "Link selected",
      description: "Paste now, or press Cmd+C or Ctrl+C while the link is selected.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" /> Share App
          </DialogTitle>
          <DialogDescription>
            Share direct access with existing Applyt users. New shares are always Editor access.
            Legacy Reader, Owner Permission, or broad access can be revoked here.
          </DialogDescription>
        </DialogHeader>

        {loading && !state ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading sharing…
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : state ? (
          <div className="space-y-5">
            {legacyPublicWarning && (
              <section className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide">
                        Legacy Broad Access
                      </Label>
                      <Badge variant="outline" className="border-amber-300 bg-white/70 text-amber-900">
                        {PUBLIC_LABEL[publicLevel]}
                      </Badge>
                    </div>
                    <p className="text-xs leading-relaxed">{legacyPublicWarning}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 border-amber-300 bg-white text-amber-950 hover:bg-amber-100"
                      onClick={() => void onDisablePublic()}
                      disabled={saving}
                    >
                      {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Disable broad access
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {/* Direct users */}
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                People with direct access
              </Label>

              <div className="rounded-md border border-border">
                <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {state.ownerEmail ?? state.ownerUserId}
                    </div>
                    <div className="text-xs text-muted-foreground">True owner</div>
                  </div>
                  <div className="text-xs text-muted-foreground">Owner</div>
                </div>
                {state.directGrants.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-muted-foreground">
                    No direct users yet.
                  </div>
                ) : (
                  state.directGrants.map((g) => (
                    <div
                      key={g.userId}
                      className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0"
                    >
                      <div className="min-w-0 text-sm">
                        <div className="truncate font-medium">{g.email ?? g.userId}</div>
                        {g.email && (
                          <div className="truncate text-xs text-muted-foreground">{g.userId}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge
                          variant="outline"
                          className={
                            g.accessLevel === "editor"
                              ? "border-primary/30 bg-primary/5 text-primary"
                              : "border-amber-300 bg-amber-50 text-amber-800"
                          }
                        >
                          {DIRECT_LABEL[g.accessLevel]}
                          {g.accessLevel !== "editor" ? " legacy" : ""}
                        </Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => void onRevoke(g.userId)}
                          disabled={pendingUserId === g.userId}
                          aria-label={`Revoke ${g.email ?? g.userId}`}
                        >
                          {pendingUserId === g.userId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={onAddGrant} className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="share-add-email" className="text-xs text-muted-foreground">
                    Add by email
                  </Label>
                  <Input
                    id="share-add-email"
                    type="email"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="h-8 text-sm"
                    disabled={saving}
                  />
                </div>
                <Button
                  type="submit"
                  size="sm"
                  className="h-8"
                  disabled={saving || !addEmail.trim()}
                >
                  {saving ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Add
                </Button>
              </form>
              <p className="text-xs text-muted-foreground">
                New users are added as Editor. They must already have an Applyt account with a
                matching profile email.
              </p>
            </section>

            <section className="space-y-2">
              <Label htmlFor="share-os-link" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                App link
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  ref={linkInputRef}
                  id="share-os-link"
                  value={shareUrl}
                  readOnly
                  className="h-8 min-w-0 text-xs"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0"
                  onClick={() => void onCopyLink()}
                >
                  <Link2 className="mr-1.5 h-4 w-4" /> Copy
                </Button>
              </div>
            </section>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
